import { useEffect, useState } from 'react'

/**
 * This module provides hooks for working with browser Navigator APIs:
 * - Permissions (e.g., geolocation, notifications)
 * - Properties (e.g., serviceWorker, userAgent)
 * - Methods (e.g., clearAppBadge, setAppBadge)
 */

// https://developer.mozilla.org/en-US/docs/Web/API/Permissions_API
/**
 * Types of permissions that can be queried using the Permissions API
 * @see https://developer.mozilla.org/en-US/docs/Web/API/Permissions_API
 */
export type TNavigatorPermissionName =
	| 'geolocation'
	| 'notifications'
	| 'persistent-storage'
	| 'push'
	| 'screen-wake-lock'

export type TNavigatorPermissionState = 'granted' | 'denied' | 'prompt'

export interface TNavigatorPermissionStatus {
	state: TNavigatorPermissionState
}

/**
 * Hook to query the status of a browser permission
 * @param permissionName - The permission to query
 * @returns Object containing the current permission status
 *
 * @example
 * ```tsx
 * const { permissionStatus } = useNavigatorPermission('notifications')
 * if (permissionStatus.state === 'granted') {
 *   // Show notifications
 * }
 * ```
 */
export const useNavigatorPermission = (
	permissionName: TNavigatorPermissionName,
) => {
	const [permissionStatus, setPermissionStatus] =
		useState<TNavigatorPermissionStatus>({ state: 'prompt' })

	useEffect(() => {
		void (async () => {
			try {
				const status = await navigator.permissions.query({
					name: permissionName,
				})
				setPermissionStatus({
					state: status.state as TNavigatorPermissionState,
				})
			} catch (err) {
				console.error(`Error getting ${permissionName} permission status:`, err)
			}
		})()
	}, [permissionName])

	return { permissionStatus }
}

// https://developer.mozilla.org/en-US/docs/Web/API/Navigator#instance_properties
/**
 * Navigator properties that can be queried
 * @see https://developer.mozilla.org/en-US/docs/Web/API/Navigator#instance_properties
 */
export type TNavigatorProperties =
	| 'serviceWorker'
	| 'userAgent'
	| 'permissions'
	| 'storage'
	| 'language'
	| 'languages'
	| 'cookieEnabled'

/**
 * Hook to check availability and access Navigator properties
 * @param propertyName - The Navigator property to query
 * @returns Object containing availability status and property value
 *
 * @example
 * ```tsx
 * const { isAvailable, value } = useNavigatorProperty('serviceWorker')
 * if (isAvailable && value) {
 *   // Use service worker
 *   value.register('/sw.js')
 * }
 * ```
 */
export const useNavigatorProperty = (propertyName: TNavigatorProperties) => {
	const [isAvailable, setIsAvailable] = useState<boolean>(false)

	useEffect(() => {
		// Check if we're in a browser environment and if the property exists
		const checkProperty = () => {
			if (typeof window !== 'undefined' && 'navigator' in window) {
				setIsAvailable(propertyName in navigator)
			} else {
				setIsAvailable(false)
			}
		}

		checkProperty()
	}, [propertyName])

	// Return both availability and the actual property value if available
	return {
		isAvailable,
		value: isAvailable ? (navigator as any)[propertyName] : undefined,
	}
}

// https://developer.mozilla.org/en-US/docs/Web/API/Navigator#instance_methods
/**
 * Navigator methods that can be called
 * @see https://developer.mozilla.org/en-US/docs/Web/API/Navigator#instance_methods
 */
export type TNavigatorMethods = 'clearAppBadge' | 'setAppBadge'

/**
 * Primary utility function to check navigator method availability
 * @param methodName - The Navigator method to check
 * @returns Object containing availability status and method caller
 */
export const checkNavigatorMethod = (methodName: TNavigatorMethods) => {
	if (
		typeof window !== 'undefined' &&
		'navigator' in window &&
		methodName in navigator
	) {
		return {
			isAvailable: true,
			call: (...args: any[]) =>
				(navigator as any)[methodName].apply(navigator, args),
		}
	}
	return {
		isAvailable: false,
		call: undefined,
	}
}

/**
 * Hook wrapper around checkNavigatorMethod that provides reactivity
 * @param methodName - The Navigator method to use
 * @returns Object containing availability status and method caller
 *
 * @example
 * ```tsx
 * const { isAvailable, call } = useNavigatorMethod('setAppBadge')
 * if (isAvailable) {
 *   // Set app badge to show 3 notifications
 *   call(3)
 * }
 * ```
 */
export const useNavigatorMethod = (methodName: TNavigatorMethods) => {
	const [state, setState] = useState(() => checkNavigatorMethod(methodName))

	useEffect(() => {
		setState(checkNavigatorMethod(methodName))
	}, [methodName])

	return state
}

// Alias for backward compatibility and direct utility use
export const callNavigatorMethod = checkNavigatorMethod
